<template>
  <TransitionRoot as="template" :show="isOpen">
    <DialogContainer
      as="div"
      class="relative z-60"
      @close="hideCancel || noDismiss || confirming ? undefined : close()">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div class="fixed inset-0 bg-neutral-900/85 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white dark:bg-neutral-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-sm sm:p-6">
              <div>
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full" :class="iconClass">
                  <div class="h-6 w-6 flex items-center justify-center">
                    <i class="text-xl fa-sharp" :class="icon" aria-hidden="true" />
                  </div>
                </div>
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-neutral-900 dark:text-white">
                    {{ heading }}
                  </DialogTitle>
                  <div class="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                    <slot />
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-6 flex gap-3 justify-stretch">
                <NtButton @click="close" :disabled="confirming" v-if="!hideCancel" class="flex-1">
                  {{ cancelLabel }}
                </NtButton>
                <NtButton
                  :level="level"
                  :loading="confirming"
                  @click="confirm"
                  :disabled="confirmDisabled"
                  class="flex-1">
                  {{ confirmLabel }}
                </NtButton>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </DialogContainer>
  </TransitionRoot>
</template>

<script>
import { Dialog as DialogContainer, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import NtButton from './NtButton.vue'

export default {
  components: {
    DialogContainer,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    NtButton
  },
  emits: ['opened', 'cancelled', 'confirmed'],
  props: {
    level: {
      type: String,
      default: 'secondary'
    },
    heading: {
      type: String,
      default: null
    },
    hideCancel: {
      type: Boolean,
      default: false
    },
    cancelLabel: {
      type: String,
      default: 'Cancel'
    },
    confirmLabel: {
      type: String,
      default: 'Confirm'
    },
    icon: {
      type: String,
      default: 'fa-sharp fa-regular fa-star'
    },
    noDismiss: {
      type: Boolean,
      default: false
    },
    autoOpen: {
      type: Boolean,
      default: false
    },
    confirmMethod: {
      type: String,
      default: null
    },
    confirmEndpoint: {
      type: String,
      default: null
    },
    confirmData: {
      type: Object,
      default: () => {}
    },
    confirmDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: this.autoOpen,
      confirming: false
    }
  },
  computed: {
    iconClass() {
      if (this.level === 'secondary') {
        return 'bg-accent-600 text-white'
      }
      if (this.level === 'danger') {
        return 'bg-red-600 text-white'
      }
      if (this.level === 'warning') {
        return 'bg-yellow-600 text-white'
      }
      if (this.level === 'success') {
        return 'bg-green-600 text-white'
      }

      return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-600 dark:text-white'
    }
  },
  methods: {
    open() {
      this.isOpen = true
      this.$emit('opened')
    },
    close() {
      this.isOpen = false
      this.$emit('cancelled')
    },
    async confirm() {
      let response = null
      if (this.confirmMethod && this.confirmEndpoint) {
        try {
          this.confirming = true
          const data = await this.$api[this.confirmMethod](
            this.confirmEndpoint,
            ['post', 'patch'].includes(this.confirmMethod)
              ? this.confirmData
              : {
                  params: this.confirmData
                }
          )

          response = data.data
        } catch (e) {
          if (e.response.status === 422) {
            this.confirming = false
            return
          }
        }
      }
      this.$emit('confirmed', response)
      this.isOpen = false
      this.confirming = false
    }
  }
}
</script>
